import { useEffect, useRef, useState } from 'react';

import {
    buildGoogleMapsEmbedUrl,
    buildGoogleMapsSearchUrl,
    isValidGoogleMapsLocation,
    loadGooglePlacesLibrary,
    mapGooglePlaceToBranch
} from '../../../../helpers/admin/branch/googleMaps';

import {
    Alert, Box, Button, CircularProgress, Divider, FormHelperText,
    Typography
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
const AUTOFILL_MESSAGE = 'Los datos disponibles se completaron automáticamente. Revísalos y realiza los ajustes necesarios antes de guardar.';

const BranchGoogleMaps = ({
    values, setValues, inputErr, setInputErr, inputRef, onAddressAutofill
}) => {
    const hostRef = useRef(null);
    const callbackRef = useRef(onAddressAutofill);
    const [status, setStatus] = useState(API_KEY ? 'loading' : 'missing-key');
    const [autofilled, setAutofilled] = useState(false);
    const googleMaps = values.links?.google_maps || {};
    const embedUrl = buildGoogleMapsEmbedUrl(googleMaps, API_KEY);
    const mapsUrl = buildGoogleMapsSearchUrl(values.name, googleMaps);

    useEffect(() => {
        callbackRef.current = onAddressAutofill;
    }, [onAddressAutofill]);

    useEffect(() => {
        let active = true;
        let autocomplete;
        let selectHandler;

        const initialize = async () => {
            try {
                const { PlaceAutocompleteElement } = await loadGooglePlacesLibrary(API_KEY);
                if (!active || !hostRef.current) return;

                autocomplete = new PlaceAutocompleteElement({});
                autocomplete.includedRegionCodes = ['mx'];
                autocomplete.includedPrimaryTypes = ['establishment'];
                autocomplete.placeholder = 'Buscar sucursal en Google Maps';
                autocomplete.style.display = 'block';
                autocomplete.style.width = '100%';
                autocomplete.setAttribute('aria-label', 'Buscar sucursal en Google Maps');

                selectHandler = async ({ placePrediction }) => {
                    try {
                        setStatus('loading-place');
                        const place = placePrediction.toPlace();
                        await place.fetchFields({
                            fields: [
                                'id', 'displayName', 'formattedAddress',
                                'addressComponents', 'location'
                            ]
                        });
                        const { fields, googleMaps: selectedGoogleMaps } = mapGooglePlaceToBranch(place);
                        if (!selectedGoogleMaps.placeId ||
                            !isValidGoogleMapsLocation(selectedGoogleMaps.location))
                            throw new Error('incomplete-google-place');

                        setValues(current => ({
                            ...current,
                            ...fields,
                            links: {
                                ...current.links,
                                google_maps: selectedGoogleMaps
                            }
                        }));
                        setInputErr(current => {
                            const next = { ...current, place_id_err: false };
                            const errorKeys = {
                                name: 'name_err', address: 'address_err', colony: 'colony_err',
                                postalCode: 'pc_err', city: 'city_err', state: 'state_err'
                            };
                            Object.keys(fields).forEach(field => {
                                if (errorKeys[field]) next[errorKeys[field]] = false;
                            });
                            return next;
                        });
                        callbackRef.current?.();
                        setAutofilled(true);
                        setStatus('ready');
                    } catch (error) {
                        setStatus('place-error');
                    }
                };

                autocomplete.addEventListener('gmp-select', selectHandler);
                hostRef.current.replaceChildren(autocomplete);
                if (inputRef) inputRef.current = autocomplete;
                setStatus('ready');
            } catch (error) {
                if (active) setStatus(API_KEY ? 'load-error' : 'missing-key');
            }
        };

        initialize();
        return () => {
            active = false;
            if (autocomplete && selectHandler)
                autocomplete.removeEventListener('gmp-select', selectHandler);
            if (inputRef?.current === autocomplete) inputRef.current = null;
            if (hostRef.current) hostRef.current.replaceChildren();
        };
    }, [inputRef, setInputErr, setValues]);

    return (
        <Box>
            <Divider textAlign="left" sx={{ my: 3, fontWeight: 'bold' }}>
                Google Maps
            </Divider>
            {/*
                Diseño de respaldo para captura manual de iframe. No está activo.
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField fullWidth variant="outlined" size="small" label="URL src del iframe" />
                    </Grid>
                    <Grid item xl={6} lg={6} md={12} sm={12} xs={12}>
                        <TextField fullWidth variant="outlined" size="small" label="Nombre de sucursal" />
                    </Grid>
                    <Grid item xl={6} lg={6} md={12} sm={12} xs={12}>
                        <TextField fullWidth variant="outlined" size="small" label="Longitud iFrame" />
                    </Grid>
                    <Grid item xl={6} lg={6} md={12} sm={12} xs={12}>
                        <TextField fullWidth variant="outlined" size="small" label="Altura iFrame" />
                    </Grid>
                </Grid>
            */}
            <Typography variant="body2" sx={{ mb: 1 }}>
                Busca y selecciona la ficha de Google Maps correspondiente a la sucursal.
            </Typography>
            {(status === 'loading' || status === 'loading-place') && (
                <Box sx={{ minHeight: 48, display: 'flex', alignItems: 'center' }}>
                    <CircularProgress size={24} />
                </Box>
            )}
            <Box ref={hostRef} sx={{ width: '100%' }} />
            {inputErr.place_id_err && (
                <FormHelperText error>Selecciona una sucursal de Google Maps.</FormHelperText>
            )}
            {status === 'missing-key' && (
                <Alert severity="error" sx={{ mt: 1 }}>
                    Falta configurar REACT_APP_GOOGLE_MAPS_API_KEY.
                </Alert>
            )}
            {status === 'load-error' && (
                <Alert severity="error" sx={{ mt: 1 }}>
                    No fue posible cargar Google Maps. Revisa la clave, las APIs habilitadas y la conexión.
                </Alert>
            )}
            {status === 'place-error' && (
                <Alert severity="error" sx={{ mt: 1 }}>
                    No fue posible obtener el identificador y las coordenadas del lugar. Intenta seleccionarlo nuevamente.
                </Alert>
            )}
            {autofilled && <Alert severity="info" sx={{ mt: 2 }}>{AUTOFILL_MESSAGE}</Alert>}

            {embedUrl ? (
                <Box sx={{ mt: 2 }}>
                    <iframe
                        src={embedUrl}
                        width="100%"
                        height="450"
                        title={`Mapa de ${values.name || 'la sucursal'}`}
                        style={{ border: 0, borderRadius: '10px' }}
                        allowFullScreen=""
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                    />
                    <Button
                        href={mapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        endIcon={<OpenInNewIcon />}
                        sx={{ mt: 1 }}
                    >
                        Ver en Google Maps
                    </Button>
                </Box>
            ) : googleMaps.placeId && status !== 'missing-key' ? (
                <Alert severity="warning" sx={{ mt: 2 }}>
                    El lugar está guardado, pero no se puede generar la vista previa sin una clave válida.
                </Alert>
            ) : null}
        </Box>
    );
};

export default BranchGoogleMaps;
